import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import i18n from "@/i18n";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";
import { detectLanguage, persistLanguageChoice } from "@/lib/languageDetector";

const LanguageContext = createContext();

const syncI18nLanguage = (lang) => {
  i18n.changeLanguage(lang);
};

const clearLanguageSensitiveState = () => {
  try {
    if (typeof window === "undefined") return;
    sessionStorage.removeItem("marstick_mobile_tab_stacks_v1");
    sessionStorage.removeItem("marstick_mobile_tab_pending_scroll_key");
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("marstick_mobile_bottom_nav_scroll_")) sessionStorage.removeItem(key);
    });
  } catch {
    // ignore private browsing/session storage issues
  }
};

export function LanguageProvider({ children }) {
  const [language, setCurrentLanguage] = useState(() => {
    return detectLanguage({
      pathname: window.location.pathname,
      search: window.location.search,
    });
  });

  const applyRouteLanguage = useCallback((newLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(newLanguage)) return;
    setCurrentLanguage(newLanguage);
    syncI18nLanguage(newLanguage);
    applyLanguageSettings(newLanguage);
  }, []);

  const setUserLanguage = useCallback((newLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(newLanguage)) return;
    clearLanguageSensitiveState();
    setCurrentLanguage(newLanguage);
    persistLanguageChoice(newLanguage);
    syncI18nLanguage(newLanguage);
    applyLanguageSettings(newLanguage);
    window.dispatchEvent(new CustomEvent("marstick:language-enforced", { detail: { language: newLanguage } }));
  }, []);

  useEffect(() => {
    applyLanguageSettings(language);
  }, [language]);

  useEffect(() => {
    const syncLanguageFromLocation = () => {
      const nextLanguage = detectLanguage({
        pathname: window.location.pathname,
        search: window.location.search,
      });
      if (!SUPPORTED_LANGUAGES.includes(nextLanguage)) return;
      setCurrentLanguage((current) => {
        if (current === nextLanguage) return current;
        syncI18nLanguage(nextLanguage);
        applyLanguageSettings(nextLanguage);
        return nextLanguage;
      });
    };

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = function pushState(...args) {
      originalPushState.apply(this, args);
      window.dispatchEvent(new Event("marstick:navigation"));
    };
    window.history.replaceState = function replaceState(...args) {
      originalReplaceState.apply(this, args);
      window.dispatchEvent(new Event("marstick:navigation"));
    };
    window.addEventListener("popstate", syncLanguageFromLocation);
    window.addEventListener("marstick:navigation", syncLanguageFromLocation);
    syncLanguageFromLocation();

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", syncLanguageFromLocation);
      window.removeEventListener("marstick:navigation", syncLanguageFromLocation);
    };
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: setUserLanguage, setUserLanguage, applyRouteLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

function applyLanguageSettings(lang) {
  // Set HTML direction and class for RTL languages
  const isRTL = lang === "he" || lang === "ar";
  document.documentElement.dir = isRTL ? "rtl" : "ltr";
  document.documentElement.lang = lang;
  
  // Toggle RTL class on html and body for comprehensive styling
  if (isRTL) {
    document.documentElement.classList.add("rtl");
    document.body.classList.add("rtl");
  } else {
    document.documentElement.classList.remove("rtl");
    document.body.classList.remove("rtl");
  }
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: detectLanguage({ pathname: window.location.pathname, search: window.location.search }),
      setLanguage: () => {},
      setUserLanguage: () => {},
      applyRouteLanguage: () => {},
    };
  }
  return context;
}